import { tenantApplicationType } from "@agentcall/core/type"
import type {
  TenantApplicationGet200TenantApplication,
  TenantApplicationGet200TenantApplicationFilesItem,
  TenantApplicationGet200TenantApplicationRoom,
} from "@/api/generated/models"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { TenantApplicationDetailView } from "./TenantApplicationDetailView"

interface TenantApplicationApprovalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantApplication: TenantApplicationGet200TenantApplication | null
  room?: TenantApplicationGet200TenantApplicationRoom | null
  files?: TenantApplicationGet200TenantApplicationFilesItem[]
  loading?: boolean
  onApprove: () => void | Promise<void>
  onReject: () => void
}

export function TenantApplicationApprovalDialog({
  open,
  onOpenChange,
  tenantApplication,
  room,
  files,
  loading = false,
  onApprove,
  onReject,
}: TenantApplicationApprovalDialogProps) {
  if (loading || !tenantApplication) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[500px] p-6 rounded-lg max-h-[90vh] overflow-y-auto">
          <div className="flex justify-center items-center min-h-[200px]">
            <Spinner />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  const isPending =
    tenantApplication.approvalStatus ===
    tenantApplicationType.TENANT_APPLICATION_APPROVAL_STATUS_PENDING
  const finalStatusLabel =
    tenantApplication.approvalStatus ===
    tenantApplicationType.TENANT_APPLICATION_APPROVAL_STATUS_APPROVED
      ? "承認済み"
      : "棄却済み"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px] p-6 rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="text-base font-semibold text-slate-900">
            新規契約者情報の確認
          </DialogTitle>
        </DialogHeader>

        <TenantApplicationDetailView
          tenantApplication={tenantApplication}
          room={room}
          files={files}
        />

        {isPending ? (
          <div className="flex flex-col gap-[20px] pt-4">
            <p className="text-[16px] font-semibold leading-[20px] text-[#354267] tracking-[-0.192px]">
              新規契約者情報に承認しますか？
            </p>
            <div className="space-y-1 text-sm leading-5 text-red-600">
              <p>※棄却した場合、契約者より再度申込フォームより申請が必要となります</p>
              <p>
                ※『ご契約情報』『物件情報』に相違がない場合は承認にて進めていただくようお願いします（部屋情報、入居者情報、画像添付は承認後に修正が可能です）
              </p>
            </div>
            <div className="flex gap-[15px] items-center justify-center">
              <Button
                variant="outline"
                onClick={onReject}
                className="flex-[1_0_0] px-[16px] py-[8px] text-[14px] font-bold leading-[24px] text-[#354267] border-[#9eafc7]"
              >
                棄却する
              </Button>
              <Button
                onClick={onApprove}
                className="flex-[1_0_0] px-[16px] py-[8px] text-[14px] font-bold leading-[24px] bg-[#354267]"
              >
                承認する
              </Button>
            </div>
          </div>
        ) : (
          <p className="pt-4 text-center text-sm font-semibold text-slate-600">
            この申請は{finalStatusLabel}です。判定結果は変更できません。
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
